import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { InvoicePrintService } from '../../core/invoice-print.service';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';

import * as XLSX from 'xlsx';

import { ApiService, Company, SalesRegister } from '../../core/api.service';
import { saveBlob } from '../../core/file-download';
import { DatePicker } from 'primeng/datepicker';

@Component({
    selector: 'app-salesreport',
    standalone: true,
    imports: [CommonModule, FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule, DialogModule, TagModule, ToastModule, ConfirmDialogModule, IconFieldModule, InputIconModule, CheckboxModule, DatePicker],
    providers: [MessageService, ConfirmationService],
    template: `
        <p-toast [style]="{ width: '32rem', 'max-width': 'calc(100vw - 2rem)' }"></p-toast>
        <p-confirmDialog></p-confirmDialog>

        <!-- Summary -->
        <div class="grid grid-cols-12 gap-8 mb-8">
            <div class="col-span-12 lg:col-span-6 xl:col-span-3">
                <div class="card mb-0">
                    <div class="flex justify-between mb-4">
                        <div>
                            <span class="block text-muted-color font-medium mb-4">Total Records</span>
                            <div class="text-surface-900 dark:text-surface-0 font-medium text-xl">{{ total }}</div>
                        </div>
                        <div class="flex items-center justify-center bg-cyan-100 dark:bg-cyan-400/10 rounded-border" style="width: 2.5rem; height: 2.5rem">
                            <i class="pi pi-chart-line text-cyan-500 text-xl"></i>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-span-12 lg:col-span-6 xl:col-span-3">
                <div class="card mb-0">
                    <div class="flex justify-between mb-4">
                        <div>
                            <span class="block text-muted-color font-medium mb-4">Selected</span>
                            <div class="text-surface-900 dark:text-surface-0 font-medium text-xl">{{ selectedIds.size }}</div>
                        </div>
                        <div class="flex items-center justify-center bg-purple-100 dark:bg-purple-400/10 rounded-border" style="width: 2.5rem; height: 2.5rem">
                            <i class="pi pi-check-square text-purple-500 text-xl"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Upload -->
        <div class="card mb-8">
            <div class="font-semibold text-xl mb-4"><i class="pi pi-upload mr-2"></i>Sales Register Upload</div>
            <div class="flex flex-wrap gap-4 items-end">
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Company</label>
                    <p-select [options]="companies" [(ngModel)]="selectedCompanyId" optionLabel="name" optionValue="id" placeholder="Select company" [filter]="true" styleClass="w-72"></p-select>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Excel File (.xlsx)</label>
                    <input type="file" accept=".xlsx,.xls" (change)="onFileSelect($event)" class="p-2 border border-surface rounded-border" />
                </div>
                <div>
                    <p-button label="Upload" icon="pi pi-cloud-upload" [loading]="uploading" [disabled]="!fileValid" (onClick)="onUpload()"></p-button>
                </div>
                <div>
                    <p-button label="Download Template" icon="pi pi-download" severity="secondary" (onClick)="downloadTemplate()"></p-button>
                </div>
            </div>
            <small *ngIf="selectedFile && fileValid" class="text-green-600 mt-3 block"><i class="pi pi-check-circle mr-1"></i>{{ selectedFile.name }} — valid file</small>
            <small *ngIf="selectedFile && !fileValid" class="text-red-500 mt-3 block"><i class="pi pi-times-circle mr-1"></i>{{ fileError }}</small>
        </div>

        <!-- Table -->
        <div class="card">
            <div class="flex flex-wrap gap-4 items-end mb-4">
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Filter by Company</label>
                    <p-select [options]="companies" [(ngModel)]="filterCompanyId" optionLabel="name" optionValue="id" placeholder="All Companies" [showClear]="true" (onChange)="onFilterChange()" styleClass="w-64"></p-select>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Filter by Region</label>
                    <p-select [options]="regions" [(ngModel)]="filterRegion" placeholder="All Regions" [showClear]="true" (onChange)="onFilterChange()" styleClass="w-56"></p-select>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">From Date</label>
                    <p-datepicker [(ngModel)]="fromDate" dateFormat="dd-mm-yy" [showIcon]="true" [showClear]="true" appendTo="body" placeholder="From" styleClass="w-44"></p-datepicker>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">To Date</label>
                    <p-datepicker [(ngModel)]="toDate" dateFormat="dd-mm-yy" [showIcon]="true" [showClear]="true" appendTo="body" placeholder="To" styleClass="w-44"></p-datepicker>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Search</label>
                    <p-iconfield iconPosition="left">
                        <p-inputicon styleClass="pi pi-search" />
                        <input type="text" pInputText [(ngModel)]="search" (keyup.enter)="onSearch()" placeholder="Customer / Product / Doc / TL / Code" class="w-72" />
                    </p-iconfield>
                </div>
                <div><p-button icon="pi pi-search" label="Search" (onClick)="onSearch()" severity="secondary"></p-button></div>
            </div>

            <div class="flex flex-wrap gap-2 items-center mb-4 p-3 rounded-border" style="background: var(--surface-100)">
                <span class="font-medium text-sm mr-2">{{ selectedIds.size }} selected</span>
                <p-button label="Select All Records" icon="pi pi-list-check" size="small" severity="secondary" (onClick)="selectAllRecords()"></p-button>
                <p-button label="Clear" icon="pi pi-times" size="small" severity="secondary" [outlined]="true" (onClick)="clearSelection()" [disabled]="selectedIds.size === 0"></p-button>
                <p-button label="Delete Selected" icon="pi pi-trash" size="small" severity="danger" (onClick)="deleteSelected()" [disabled]="selectedIds.size === 0"></p-button>
                <p-button label="Print Invoice" icon="pi pi-print" size="small" (onClick)="printInvoice()" [disabled]="selectedIds.size === 0"></p-button>
            </div>

            <p-table
                [value]="records"
                [lazy]="true"
                (onLazyLoad)="onPage($any($event))"
                [paginator]="true"
                [rows]="pageSize"
                [totalRecords]="total"
                [loading]="loading"
                [rowsPerPageOptions]="[10, 20, 50]"
                [scrollable]="true"
                scrollHeight="500px"
                [showCurrentPageReport]="true"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                styleClass="p-datatable-sm p-datatable-striped"
                [tableStyle]="{ 'min-width': '90rem' }"
            >
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width:3rem" class="text-center">
                            <p-checkbox [binary]="true" [ngModel]="isPageAllSelected()" (onChange)="togglePage($event)"></p-checkbox>
                        </th>
                        <th style="width:4rem">Sr</th>
                        <th>Customer</th>
                        <th>Product</th>
                        <th>Doc #</th>
                        <th>From</th>
                        <th>Cust Code</th>
                        <th>Region</th>
                        <th>TL #</th>
                        <th>Doc Date</th>
                        <th>Qty Ltrs</th>
                        <th>Unit Price</th>
                        <th>Value Excl Tax</th>
                        <th>Net Sales</th>
                        <th>Total Recv.</th>
                        <th style="width:8rem">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr [class.bg-primary-50]="selectedIds.has(row.id)">
                        <td class="text-center"><p-checkbox [binary]="true" [ngModel]="selectedIds.has(row.id)" (onChange)="toggleRow(row.id)"></p-checkbox></td>
                        <td>{{ row.sr }}</td>
                        <td>{{ row.customerName }}</td>
                        <td><p-tag *ngIf="row.product" [value]="row.product" severity="info"></p-tag></td>
                        <td>{{ row.documentNumber }}</td>
                        <td>{{ row.fromLocation }}</td>
                        <td>{{ row.customerCode }}</td>
                    <td>{{ row.region }}</td>
                        <td>{{ row.tlNumber }}</td>
                        <td>{{ row.documentDate ? (row.documentDate | date: 'dd-MMM-yyyy') : '' }}</td>
                        <td>{{ row.quantityLiters | number: '1.0-2' }}</td>
                        <td>{{ row.unitPrice | number: '1.0-4' }}</td>
                        <td>{{ row.valueOfSalesExclTax | number: '1.0-2' }}</td>
                        <td>{{ row.netSales | number: '1.0-2' }}</td>
                        <td>{{ row.totalReceivable | number: '1.0-2' }}</td>
                        <td>
                            <p-button icon="pi pi-pencil" severity="info" [text]="true" (onClick)="openEdit(row)"></p-button>
                            <p-button icon="pi pi-trash" severity="danger" [text]="true" (onClick)="confirmDelete(row)"></p-button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="16" class="text-center p-8 text-muted-color"><i class="pi pi-inbox text-2xl block mb-2"></i>No records found. Upload an Excel file.</td>
                    </tr>
                </ng-template>
            </p-table>
        </div>

        <!-- Edit dialog -->
        <p-dialog header="Edit Sales Record" [(visible)]="editDialog" [modal]="true" [style]="{ width: '800px' }" [draggable]="false" [maximizable]="true">
            <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Customer Name</label><input pInputText [(ngModel)]="editItem.customerName" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Product</label><input pInputText [(ngModel)]="editItem.product" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Document Number</label><input pInputText [(ngModel)]="editItem.documentNumber" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">From</label><input pInputText [(ngModel)]="editItem.fromLocation" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Customer Code</label><input pInputText [(ngModel)]="editItem.customerCode" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">NTN</label><input pInputText [(ngModel)]="editItem.ntn" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">TL #</label><input pInputText [(ngModel)]="editItem.tlNumber" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">FN</label><input pInputText [(ngModel)]="editItem.fn" /></div>
                <div class="flex flex-col gap-2 col-span-2"><label class="text-sm font-medium">Address</label><input pInputText [(ngModel)]="editItem.address" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Quantity Liters</label><p-inputnumber [(ngModel)]="editItem.quantityLiters" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Unit Price</label><p-inputnumber [(ngModel)]="editItem.unitPrice" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Value Excl Tax</label><p-inputnumber [(ngModel)]="editItem.valueOfSalesExclTax" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Sales Tax 18%</label><p-inputnumber [(ngModel)]="editItem.salesTax18" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Extra Margin</label><p-inputnumber [(ngModel)]="editItem.extraMargin" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Franchise Fee</label><p-inputnumber [(ngModel)]="editItem.franchiseFee" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">FED</label><p-inputnumber [(ngModel)]="editItem.fed" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Dealer Commission</label><p-inputnumber [(ngModel)]="editItem.dealerCommission" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium">Withholding on Dealer Comm.</label><p-inputnumber [(ngModel)]="editItem.withholdingOnDealerCommission" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber>
                </div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Total Receivable</label><p-inputnumber [(ngModel)]="editItem.totalReceivable" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Net Sales</label><p-inputnumber [(ngModel)]="editItem.netSales" [useGrouping]="false" [maxFractionDigits]="2"></p-inputnumber></div>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="editDialog = false"></p-button>
                <p-button label="Save" icon="pi pi-check" (onClick)="saveEdit()"></p-button>
            </ng-template>
        </p-dialog>
    `
})
export class SalesReport implements OnInit {
    companies: Company[] = [];
    selectedCompanyId: number | null = null;
    filterCompanyId: number | null = null;
    fromDate: Date | null = null;
    toDate: Date | null = null;

    selectedFile: File | null = null;
    uploading = false;
    fileValid = false;
    fileError = '';

    records: SalesRegister[] = [];
    total = 0;
    page = 1;
    pageSize = 10;
    loading = false;
    search = '';

    selectedIds = new Set<number>();

    editDialog = false;
    editItem: SalesRegister = {} as SalesRegister;

    // backend se aati hai (GET /api/salesregister/template-headers) - template + parser ke saath single source of truth
    private expectedHeaders: string[] | null = null;

    regions: string[] = [];
    filterRegion: string | null = null;

    constructor(
        private api: ApiService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private cd: ChangeDetectorRef,
        private invoicePrint: InvoicePrintService
    ) {}

    ngOnInit(): void {
        this.loadCompanies();
        this.loadExpectedHeaders();
        this.loadRegions();
        this.loadData();
    }

    loadExpectedHeaders(): void {
        this.api.getSalesTemplateHeaders().subscribe({
            next: (headers) => {
                this.expectedHeaders = headers;
                this.cd.detectChanges();
            },
            error: () => {
                this.expectedHeaders = null;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load the expected file format.' });
                this.cd.detectChanges();
            }
        });
    }

    loadRegions(): void {
        this.api.getSalesRegions().subscribe({
            next: (data) => {
                this.regions = data;
                this.cd.detectChanges();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load regions.' })
        });
    }

    downloadTemplate(): void {
        this.api.downloadSalesTemplate().subscribe({
            next: (blob) => saveBlob(blob, 'Sales_Register_Template.xlsx'),
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not download template.' })
        });
    }

    loadCompanies(): void {
        this.api.getCompanies().subscribe({
            next: (data) => {
                this.companies = data;
                this.cd.detectChanges();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load companies.' })
        });
    }

    private norm(s: any): string {
        return String(s ?? '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ');
    }

    onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.selectedFile = input.files && input.files.length ? input.files[0] : null;
        this.fileValid = false;
        this.fileError = '';
        if (!this.selectedFile) {
            this.cd.detectChanges();
            return;
        }
        if (!this.expectedHeaders) {
            // header list load nahi hui -> validate nahi kar sakte, upload disabled rahega
            this.fileError = 'Could not load the expected file format.';
            this.cd.detectChanges();
            return;
        }
        const expectedHeaders = this.expectedHeaders;

        const reader = new FileReader();
        reader.onload = (e: any) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
                const headerRow = rows && rows.length > 0 ? rows[0] : [];
                const actual = headerRow.map((h) => this.norm(h)).filter((h) => h !== '');
                const expected = expectedHeaders.map((h) => this.norm(h));

                if (actual.length < expected.length) {
                    this.fileError = `Invalid file: expected at least ${expected.length} columns but found ${actual.length}.`;
                    this.cd.detectChanges();
                    return;
                }
                const mismatches: string[] = [];
                for (let i = 0; i < expected.length; i++) {
                    if (actual[i] !== expected[i]) mismatches.push(`Column ${i + 1}: expected "${expectedHeaders[i]}", found "${headerRow[i] ?? '(empty)'}"`);
                }
                if (mismatches.length > 0) {
                    this.fileError = 'Invalid format. ' + mismatches.slice(0, 3).join('; ') + (mismatches.length > 3 ? ' ...' : '');
                    this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: 'Excel columns do not match the required format.' });
                    this.cd.detectChanges();
                    return;
                }
                this.fileValid = true;
                this.fileError = '';
                this.cd.detectChanges();
            } catch {
                this.fileError = 'Could not read the file.';
                this.cd.detectChanges();
            }
        };
        reader.readAsArrayBuffer(this.selectedFile);
    }

    onUpload(): void {
        if (!this.selectedCompanyId) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Please select a company first.' });
            return;
        }
        if (!this.selectedFile) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Please select an Excel file.' });
            return;
        }
        if (!this.fileValid) {
            this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: this.fileError });
            return;
        }

        this.uploading = true;
        this.api.uploadSales(this.selectedCompanyId, this.selectedFile).subscribe({
            next: (res: any) => {
                this.uploading = false;
                this.selectedFile = null;
                this.fileValid = false;
                this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Uploaded.' });
                this.filterCompanyId = this.selectedCompanyId;
                this.page = 1;
                this.clearSelection();
                this.loadRegions();
                this.loadData();
                this.cd.detectChanges();
            },
            error: (err) => {
                this.uploading = false;
                // rejection message lambi ho sakti hai (row list) -> zyada der dikhao
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Upload failed.', life: 20000 });
                this.cd.detectChanges();
            }
        });
    }

    loadData(): void {
        this.loading = true;
        this.api.getSalesList(this.filterCompanyId, this.page, this.pageSize, this.search, this.toIso(this.fromDate), this.toIso(this.toDate), this.filterRegion).subscribe({
            next: (res) => {
                this.records = res.data;
                this.total = res.total;
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load data.' });
                this.cd.detectChanges();
            }
        });
    }

    onPage(event: TablePageEvent): void {
        this.page = Math.floor(event.first / event.rows) + 1;
        this.pageSize = event.rows;
        this.loadData();
    }
    onFilterChange(): void {
        this.page = 1;
        this.clearSelection();
        this.loadData();
    }
    onSearch(): void {
        this.page = 1;
        this.clearSelection();
        this.loadData();
    }

    toggleRow(id: number): void {
        this.selectedIds.has(id) ? this.selectedIds.delete(id) : this.selectedIds.add(id);
        this.cd.detectChanges();
    }
    isPageAllSelected(): boolean {
        return this.records.length > 0 && this.records.every((r) => this.selectedIds.has(r.id));
    }
    togglePage(event: any): void {
        const checked = event?.checked ?? !this.isPageAllSelected();
        if (checked) this.records.forEach((r) => this.selectedIds.add(r.id));
        else this.records.forEach((r) => this.selectedIds.delete(r.id));
        this.cd.detectChanges();
    }
    selectAllRecords(): void {
        this.api.getSalesIds(this.filterCompanyId, this.search, this.toIso(this.fromDate), this.toIso(this.toDate), this.filterRegion).subscribe({
            next: (ids) => {
                ids.forEach((id) => this.selectedIds.add(id));
                this.messageService.add({ severity: 'info', summary: 'Selected', detail: `${ids.length} record(s) selected.` });
                this.cd.detectChanges();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not select all.' })
        });
    }
    clearSelection(): void {
        this.selectedIds.clear();
        this.cd.detectChanges();
    }

    deleteSelected(): void {
        if (this.selectedIds.size === 0) return;
        const ids = Array.from(this.selectedIds);
        this.confirmationService.confirm({
            message: `Delete ${ids.length} selected record(s)?`,
            header: 'Confirm Bulk Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, delete all',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.bulkDeleteSales(ids).subscribe({
                    next: (res: any) => {
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: res?.message || 'Deleted.' });
                        this.clearSelection();
                        this.loadData();
                        this.cd.detectChanges();
                    },
                    error: () => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Bulk delete failed.' });
                        this.cd.detectChanges();
                    }
                });
            }
        });
    }

    openEdit(item: SalesRegister): void {
        this.editItem = { ...item };
        this.editDialog = true;
    }
    saveEdit(): void {
        this.api.updateSales(this.editItem.id, this.editItem).subscribe({
            next: () => {
                this.editDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Record updated.' });
                this.loadData();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Update failed.' });
                this.cd.detectChanges();
            }
        });
    }
    confirmDelete(item: SalesRegister): void {
        this.confirmationService.confirm({
            message: `Delete "${item.customerName || 'this record'}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, delete',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.deleteSales(item.id).subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Record deleted.' });
                        this.selectedIds.delete(item.id);
                        this.loadData();
                        this.cd.detectChanges();
                    },
                    error: () => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Delete failed.' });
                        this.cd.detectChanges();
                    }
                });
            }
        });
    }

    printInvoice(): void {
        if (this.selectedIds.size === 0) return;
        // selected ke poore records (har page se)
        this.api.getSalesList(this.filterCompanyId, 1, 100000, this.search, this.toIso(this.fromDate), this.toIso(this.toDate), this.filterRegion).subscribe({
            next: (res) => {
                const rows = res.data.filter((r) => this.selectedIds.has(r.id));
                if (rows.length === 0) return;
                this.invoicePrint.print(rows, this.companies);
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load records for printing.' })
        });
    }
    private toIso(d: Date | null): string | null {
        if (!d) return null;
        // local date -> yyyy-mm-dd (timezone shift se bachne ke liye)
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
}
